import { useEffect, useRef, useState, type ReactNode } from 'react';
import dayjs from 'dayjs';
import { Check, Lock, Pencil, Trash2, X } from 'lucide-react';
import { Button, DatePicker, Input, InputNumber, Select, Spinner, Textarea, Tooltip, cn } from '../ui';
import { FileUploadField, type UploadCategory } from './FileUploadField';

export type InlineEditType = 'text' | 'number' | 'select' | 'date' | 'textarea' | 'file';

export interface InlineEditFieldProps {
  label: string;
  /** Keep the label for screen readers but not on screen — for a field whose card already names it. */
  hideLabel?: boolean;
  /** Read-only presentation. Withhold it (undefined) to get the em-dash placeholder. */
  children?: ReactNode;
  /** Current raw value, seeded into the editor when it opens. */
  value?: string | number | string[] | null;
  /** False renders exactly as before — no affordance, no hover state. */
  editable?: boolean;
  /** Why it is locked. Shown on a small padlock so "why can't I edit this?" has an answer. */
  lockReason?: string;
  type?: InlineEditType;
  options?: { value: string | number; label: string }[];
  /** For `type="file"`: which upload bucket the document belongs to. */
  uploadCategory?: UploadCategory;
  /** For `type="select"`: render a richer row than the bare label (e.g. name + coverage area). */
  optionRender?: (option: any) => ReactNode;
  /** For `type="select"`: pick several (e.g. a farmer's crops). Saves an ARRAY. */
  multiple?: boolean;
  placeholder?: string;
  /** Shown as a confirmation before saving — e.g. that this edit re-opens KYC. */
  confirm?: string;
  /** Resolve to save, reject to keep the editor open with the error. */
  onSave: (value: any) => Promise<unknown>;
  className?: string;
}

/**
 * One field of a record, readable by everyone and editable in place by whoever is allowed.
 *
 * Replaces "open the edit modal to change one thing". The modal made every edit look equally
 * privileged — it asked for `RETAILER.EDIT` once and then showed the credit limit next to the
 * contact person — whereas editing in place lets each field carry its OWN authority, which is what
 * the permission model actually says.
 *
 * `editable` is decided by the SERVER (the record's `editableFields`), never by the client reading
 * role names: a user can hold several roles, and hiding a control is a hint, not a guard.
 */
export function InlineEditField({
  label, hideLabel, children, value, editable, lockReason, type = 'text', options, uploadCategory, optionRender, multiple, placeholder, confirm,
  onSave, className,
}: InlineEditFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<any>(value ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) return;
    setDraft(multiple ? (Array.isArray(value) ? [...value] : []) : (value ?? ''));
    setError(null); setConfirming(false);
  }, [editing, value, multiple]);

  const close = () => { setEditing(false); setError(null); setConfirming(false); };

  const commit = async () => {
    // An unchanged value is not an edit — saving it would touch `updatedBy`, write an audit row and,
    // for a KYC field, needlessly re-open verification.
    const norm = (v: any) =>
      Array.isArray(v) ? JSON.stringify([...v].map(String).sort()) : (v === null || v === undefined ? '' : String(v).trim());
    if (norm(draft) === norm(multiple ? (Array.isArray(value) ? value : []) : value)) { close(); return; }
    if (confirm && !confirming) { setConfirming(true); return; }
    setBusy(true); setError(null);
    try {
      await onSave(
        multiple ? (Array.isArray(draft) ? draft : [])
          : type === 'number' ? (draft === '' || draft === null ? null : Number(draft))
            : draft,
      );
      close();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? e?.message ?? 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    // Enter saves single-line editors; a textarea keeps Enter for newlines.
    if (e.key === 'Enter' && type !== 'textarea') { e.preventDefault(); void commit(); }
  };

  if (!editing) {
    /*
     * A DOCUMENT IS LOOKED AT FAR MORE OFTEN THAN IT IS REPLACED.
     *
     * Every read view used to be one big edit button, which for a `file` field meant the thumbnail
     * inside it never received a click: opening a dealer's GST certificate put you in the UPLOAD
     * editor, and you then had to click the same image AGAIN, inside the editor, to see it. (It was
     * also an <a>/preview trigger nested inside a <button>, which is invalid and is exactly why the
     * outer control swallowed the click.)
     *
     * So a file slot that HOLDS a document previews on click — the thumbnail's own zoom, the PDF's
     * own "Open" link — and editing moves to an explicit pencil in the slot's top-right corner. An
     * EMPTY slot keeps the whole-row button: there is nothing to preview, and "click here to add
     * one" is the only thing it can usefully do.
     */
    const previewFirst = !!editable && type === 'file' && children != null;
    /*
     * BESIDE THE THING IT EDITS — never at the cell's far edge.
     *
     * Pushed right with `ml-auto` the pencil landed at the end of a ~250px grid column while its
     * document sat 70px wide at the left, so it read as belonging to the NEXT slot's label. It now
     * sits immediately after the label (or, when the caller hides the label, immediately beside the
     * document itself), where nothing else can claim it.
     */
    const pencil = (
      <Tooltip label={`Edit ${label}`}>
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label={`Edit ${label}`}
          /* A BUTTON, drawn as one: a hairline box and the brand's own green, so it reads as
             something to press rather than as a grey mark printed after the label. Border only, no
             shadow — one separator per element. `-my-1` keeps the 24px control from stretching the
             11px caption line it sits on. */
          className="-my-1 grid size-6 shrink-0 place-items-center rounded-control border border-line bg-surface text-brand-700 transition-colors hover:border-brand-400 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:text-brand-300 dark:hover:border-brand-500 dark:hover:bg-brand-900/30"
        >
          <Pencil size={12} />
        </button>
      </Tooltip>
    );
    return (
      <div className={className}>
        <dt className={cn('flex items-center gap-1 type-caption text-subtle', hideLabel && 'sr-only')}>
          {label}
          {!editable && lockReason && (
            <Tooltip title={lockReason}>
              <Lock size={11} className="shrink-0 text-subtle/70" aria-label={lockReason} />
            </Tooltip>
          )}
          {previewFirst && !hideLabel && pencil}
        </dt>
        <dd className="mt-1 type-body-strong text-content">
          {previewFirst ? (
            // With no label to sit beside, the pencil rides alongside the document — top-aligned, so
            // it reads as that thumbnail's own corner action rather than as a caption under it.
            hideLabel ? <span className="flex items-start gap-1.5">{children}{pencil}</span> : children
          ) : editable ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              // `group` + a hover tint rather than a permanently visible pencil: 20 always-on pencils
              // turn a profile into a form, which is the thing this replaced.
              className="group -mx-1.5 -my-1 flex w-full items-center gap-1.5 rounded-control px-1.5 py-1 text-left transition hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              aria-label={`Edit ${label}`}
            >
              <span className="min-w-0 flex-1">{children ?? <span className="font-normal text-subtle">—</span>}</span>
              <Pencil size={12} className="shrink-0 text-subtle opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100" />
            </button>
          ) : (
            children ?? <span className="font-normal text-subtle">—</span>
          )}
        </dd>
      </div>
    );
  }

  // The kit's controls do not share a size vocabulary: Input/Textarea take 'sm', while the
  // antd-compat InputNumber/DatePicker take 'small'. Kept explicit per control rather than
  // spread from one object, which is what made them disagree at the type level.
  // `aria-label` rides along with the size/disabled props for the same reason: the field's name is
  // rendered in the <dt> above, which is not a <label> and so names nothing. Every editor below is
  // announced by the field it edits.
  const text = { autoFocus: true, size: 'sm' as const, disabled: busy, 'aria-label': label };
  const compat = { autoFocus: true, size: 'small' as const, disabled: busy, 'aria-label': label };
  return (
    <div className={className} ref={wrap} onKeyDown={onKey}>
      <dt className={cn('type-caption text-subtle', hideLabel && 'sr-only')}>{label}</dt>
      <dd className="mt-1">
        <div className={cn('gap-1.5', type === 'file' ? 'flex flex-col items-start' : 'flex items-start')}>
          <div className="min-w-0 flex-1">
            {type === 'file' ? (
              <FileUploadField category={uploadCategory ?? 'attachment'} size="small" disabled={busy}
                value={String(draft ?? '')} onChange={(url: string) => setDraft(url ?? '')} />
            ) : type === 'select' ? (
              <Select {...text} mode={multiple ? 'multiple' : undefined}
                value={multiple ? (Array.isArray(draft) ? draft : []) : (draft === '' ? undefined : draft)}
                options={options} showSearch allowClear optionRender={optionRender} placeholder={placeholder}
                onChange={(v: any) => setDraft(multiple ? (v ?? []) : (v ?? ''))} className="w-full" />
            ) : type === 'number' ? (
              <InputNumber {...compat} value={draft === '' ? null : Number(draft)} placeholder={placeholder}
                onChange={(v: any) => setDraft(v ?? '')} style={{ width: '100%' }} />
            ) : type === 'date' ? (
              <DatePicker {...compat} format="DD MMM YYYY" value={draft ? dayjs(String(draft)) : null}
                onChange={(d: any) => setDraft(d ? d.format('YYYY-MM-DD') : '')} style={{ width: '100%' }} />
            ) : type === 'textarea' ? (
              <Textarea {...text} rows={2} value={String(draft ?? '')} placeholder={placeholder}
                onChange={(e: any) => setDraft(e.target.value)} className="w-full" />
            ) : (
              <Input {...text} value={String(draft ?? '')} placeholder={placeholder}
                onChange={(e: any) => setDraft(e.target.value)} className="w-full" />
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="primary" onClick={() => void commit()} disabled={busy}
              icon={busy ? <Spinner /> : <Check size={14} />} aria-label="Save" />
            {/* Discarding is red so the pair reads at a glance: green commits, red throws away.
                `soft-danger` (red icon on a light red fill) rather than a solid red block — two solid
                fills side by side compete, and cancel is the secondary action of the two. */}
            <Button size="sm" variant="soft-danger" onClick={close} disabled={busy} icon={<X size={14} />} aria-label="Cancel" />
            {/* Replacing a document is only half of it — a wrong file must be removable, or the only
                way out of a mis-upload is to overwrite it with another wrong file. */}
            {type === 'file' && value && (
              <Button size="sm" variant="soft-danger" onClick={() => setDraft('')} disabled={busy}
                icon={<Trash2 size={14} />} aria-label={`Remove ${label}`} />
            )}
          </div>
        </div>
        {confirming && !error && (
          <p className="mt-1.5 rounded-control bg-warning-500/10 px-2 py-1 type-caption text-warning-600 dark:text-harvest-300">
            {confirm} <span className="font-semibold">Save again to confirm.</span>
          </p>
        )}
        {error && <p className="mt-1.5 type-caption text-danger-600 dark:text-danger-500">{error}</p>}
      </dd>
    </div>
  );
}
